import { useEffect, useState, useCallback } from "react";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import Table from "./Table";
import { Modal } from "../../shared-components/Modal";
import {
  ListViewControl,
  useControlGroupContext,
} from "../../api/ControlGroup";
import FilterComponent from "../../shared-components/Filter";

import {
  useGetCompletedFitlerList,
  useGetReportAdminCompletedList,
} from "../../api/ReportsApi";

import { ReportsAdminCompletedInput } from "../../api/Reports";
import { LoadedSince } from "../../shared-components/LoadedSince";
import LoadingSpinner from "../../shared-components/LoadingSpinner";

import { getFilterValues, hasFilterValues } from "./AdminUtil";
import CompletedTableFilter from "./CompletedTableFilter";
import InputField from "../../shared-components/InputField";
import { classNames } from "../../utils";
import { SearchIcon } from "../../shared-components/icons";
import PaginationInMemory from "../../shared-components/PaginationInMemory";

const initialFilter = {
  behavior_kit_uuid: [],
  blueprint_uuid: [],
};

const AdminCompleted = () => {
  const {
    isLoading: loadingCompleted,
    data: completedData,
    mutate: getCompletedList,
    error: errorGettingCompleted,
  } = useGetReportAdminCompletedList();
  const {
    isLoading: loadingFilter,
    data: filterData,
    mutate: getFilterData,
  } = useGetCompletedFitlerList();

  const pageSize = 25;
  const [total, setTotal] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<number | undefined>(undefined);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [backup, setBackup] = useState<ListViewControl[] | null>(null);
  const [orderBy, setOrderBy] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  const [searchName, setSearchName] = useState("");
  const [prevSearchName, setPrevSearchName] = useState("");

  const { controlGroups, setControlGroups, getControlByName } =
    useControlGroupContext();
  const [filter, setFilter] = useState<{ [key: string]: string[] }>(
    initialFilter
  );

  function fetchCompletedListBaseProps(
    newFilter?: {
      company_uuid?: string[];
      behavior_kit_uuid?: string[];
      blueprint_uuid?: string[];
      boolean_search?: string[];
    },
    initialSearchName?: boolean
  ) {
    const fetchAttr: ReportsAdminCompletedInput = {
      page_size: pageSize,
      page: page - 1,
    };

    // check if there is at least one key in the newFilter object
    let filterCount = 0;
    const filterToSend = newFilter ? newFilter : filter;
    if (filterToSend) {
      const keys = Object.keys(filterToSend);
      if (keys.length > 0) {
        filterCount = keys.reduce((acc, key) => {
          return (
            filterToSend[key as keyof typeof filterToSend]?.length || 0 + acc
          );
        }, 0);
      }
    }

    if (filterCount > 0) {
      fetchAttr.filter = {
        ...filterToSend,
      };
    } else {
      fetchAttr.filter = {};
    }

    if (filterToSend["company_uuid"]?.length === 0) {
      delete filterToSend["company_uuid"];
    }

    if (searchName && !initialSearchName) {
      fetchAttr.filter["boolean_search"] = [searchName];
      filterToSend["boolean_search"] = [searchName];
    } else {
      delete fetchAttr.filter["boolean_search"];
      delete filterToSend["boolean_search"];
    }

    getCompletedList(fetchAttr);
    getFilterData(filterToSend);
  }

  const fetchCompletedList = useCallback(() => {
    fetchCompletedListBaseProps();
    setControlGroups([
      { name: "company_uuid", controls: { direction: "", filters: [] } },
      { name: "behavior_kit_uuid", controls: { direction: "", filters: [] } },
      { name: "blueprint_uuid", controls: { direction: "", filters: [] } },
    ]);
    // eslint-disable-next-line
  }, [getCompletedList, getFilterData, orderBy]);

  useEffect(() => {
    fetchCompletedList();
  }, [fetchCompletedList]);

  useEffect(() => {
    if (completedData) {
      setTotal(completedData?.total_rows ?? 1);
      setLastUpdated(new Date().getTime());
    }
  }, [completedData]);

  useEffect(() => {
    fetchCompletedListBaseProps();
    // eslint-disable-next-line
  }, [page]);

  const openModalByName = (name: string) => {
    setBackup(structuredClone(controlGroups));
    setOpenModal(name);
  };

  const handleOrderByChange = (name: string, action: "add" | "remove") => {
    setOrderBy((prevOrderBy) => {
      const newSet = new Set(prevOrderBy);
      action === "add" ? newSet.add(name) : newSet.delete(name);
      return newSet;
    });
  };

  const handleClearAll = () => {
    setControlGroups([
      { name: "company_uuid", controls: { direction: "", filters: [] } },
      { name: "behavior_kit_uuid", controls: { direction: "", filters: [] } },
      { name: "blueprint_uuid", controls: { direction: "", filters: [] } },
    ]);
    setFilter(initialFilter);
    setPage(1);
    setSearchName("");
    setPrevSearchName("");
    fetchCompletedListBaseProps(initialFilter, true);
  };

  const hasFilters = (name: string): boolean => {
    const control = controlGroups.find((control) => control.name === name);
    return control
      ? control.controls.filters?.length > 0 ||
          control.controls.direction?.trim() !== ""
      : false;
  };

  const handleClose = () => {
    if (backup) {
      setControlGroups(backup);
    }
    setOpenModal(null);
  };

  const handleSaveFilter = (
    page: number,
    controlSaved?: string,
    action: "add" | "remove" = "add"
  ) => {
    if (controlSaved) {
      if (getControlByName(controlSaved)?.controls.direction) {
        handleOrderByChange(controlSaved, action);
      }
    }

    const newFilter = {
      ...filter,
    };
    if (controlSaved && action === "remove") {
      newFilter[controlSaved] = [];
    }

    if (controlSaved && action === "add") {
      newFilter[controlSaved] =
        controlGroups.find((group) => group.name === controlSaved)?.controls
          .filters ?? [];
    }

    setFilter(newFilter);
    fetchCompletedListBaseProps(newFilter);
    setOpenModal(null);
  };

  if (errorGettingCompleted) {
    return (
      <ErrorMessageContainer message="Couldn't load Completed reports list" />
    );
  }

  const tableHeader = [
    { name: "NAME / COMPANY / ID" },
    { name: "PARENT COMPANY" },
    { name: "COMPLETED / ADDED" },
    { name: "MET SLA?" },
    { name: "CHECK TYPE / BEHAVIOR KIT" },
    { name: "ACTIONS" },
  ];

  tableHeader.push({ name: "DELETE" });

  function clearCache() {
    fetchCompletedListBaseProps();
  }

  function handleInputAction() {
    const newFilter = {
      ...filter,
    };

    if (prevSearchName === searchName) {
      return;
    }
    setPrevSearchName(searchName);

    if (searchName) {
      newFilter["boolean_search"] = [searchName];
    } else {
      delete newFilter["boolean_search"];
    }
    fetchCompletedListBaseProps(newFilter);
  }

  const hasCompanyFilter = hasFilterValues(controlGroups, "company_uuid");

  const hasBehaviorKitFilter = hasFilterValues(
    controlGroups,
    "behavior_kit_uuid"
  );
  const hasCheckTypeFilter = hasFilterValues(controlGroups, "blueprint_uuid");

  const companyFilterValues = getFilterValues(
    controlGroups,
    "company_uuid",
    filterData
  );

  const behaviorKitFilterValues = getFilterValues(
    controlGroups,
    "behavior_kit_uuid",
    filterData
  );

  const checkTypeFilterValues = getFilterValues(
    controlGroups,
    "blueprint_uuid",
    filterData
  );

  return (
    <>
      <div
        className={classNames(
          "relative flex flex-row flex-wrap gap-2 mb-2 bg-white p-4 rounded-lg border border-gray-200 items-center",
          loadingFilter ? "opacity-50 pointer-events-none" : ""
        )}
      >
        <div className="absolute right-2 top-[-1.5rem] w-48 h-3">
          <LoadedSince
            reportData={{ timestamp: lastUpdated }}
            onClear={clearCache}
          />
        </div>

        <>
          <CompletedTableFilter
            hasFilter={hasCompanyFilter}
            filterValues={companyFilterValues}
            setOpenModal={setOpenModal}
            title="Please select a Company"
            modalKey="company_uuid"
          />

          <CompletedTableFilter
            hasFilter={hasCheckTypeFilter}
            filterValues={checkTypeFilterValues}
            setOpenModal={setOpenModal}
            title=" Please select a Check Type"
            modalKey="blueprint_uuid"
          />
          <CompletedTableFilter
            hasFilter={hasBehaviorKitFilter}
            filterValues={behaviorKitFilterValues}
            setOpenModal={setOpenModal}
            title=" Please select a Behavior Kit"
            modalKey="behavior_kit_uuid"
          />
          <div className="flex flex-row w-80 items-center relative">
            <InputField
              handleChange={(e) => {
                setSearchName(e.target.value);
              }}
              handleBlur={handleInputAction}
              handleEnter={handleInputAction}
              name={"name"}
              placeholder="Search Name"
              label={""}
              className="w-full pr-10"
              value={searchName}
            />
            <SearchIcon className="w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <button
            className="border-none underline text-sm text-gray-500  ml-3 focus:outline-none"
            onClick={handleClearAll}
          >
            Clear All
          </button>
        </>

        {loadingFilter && <LoadingSpinner />}
      </div>
      <div>
        <Table
          isLoading={loadingCompleted}
          header={tableHeader}
          openFilter={openModalByName}
          hasFilters={hasFilters}
          data={completedData?.report_summaries ?? []}
        />
      </div>
      {!loadingCompleted && (
        <PaginationInMemory
          currentPage={page}
          pageSize={pageSize}
          totalCount={total}
          pageChange={(newPageIdx) => {
            setPage(newPageIdx);
          }}
        />
      )}

      {filterData &&
        openModal &&
        filterData[openModal] &&
        controlGroups.map((group, id) => (
          <Modal
            key={id}
            open={openModal === group.name}
            width="w-[600px]"
            onClose={handleClose}
          >
            <FilterComponent
              controlGroup={group}
              date={false}
              parentList={filterData[openModal]}
              loading={loadingFilter}
              saveFilter={handleSaveFilter}
              cleared={() => {
                handleSaveFilter(1, group.name, "remove");
              }}
              showSortHeader={false}
              canceled={handleClose}
            />
          </Modal>
        ))}
    </>
  );
};

export default AdminCompleted;
